import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Send, Phone, Mail, MapPin } from "lucide-react";
import { BRAND } from "@/data/content";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Contact = () => {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    destination: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Please share your name and email so we can reach you.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Thank you — a Royal Tours curator will reach out within one business day.");
      setForm({ name: "", email: "", phone: "", destination: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again or call us directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="bg-[var(--soft)] py-14 border-t border-gray-200">
      <div className="max-w-[1280px] mx-auto px-4 lg:px-6 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="text-[var(--red)] font-semibold text-[11px] tracking-[0.25em] mb-2">
            QUICK ENQUIRY
          </div>
          <h2 className="font-serif-display text-4xl md:text-5xl text-[var(--navy)] leading-tight">
            Talk to a <span className="text-[var(--yellow-2)]">Royal Tours</span> expert.
          </h2>
          <p className="text-gray-600 mt-3 text-sm md:text-base max-w-md">
            Share a few details and a senior travel curator will design a
            tailored proposal within 24 hours — no obligations, no templates.
          </p>

          <div className="mt-8 space-y-4">
            <a href={`tel:${BRAND.phone.replace(/\s/g, "")}`} data-testid="contact-phone-link" className="flex items-start gap-3 group">
              <div className="w-10 h-10 rounded-full bg-[var(--yellow)] flex items-center justify-center shrink-0">
                <Phone className="w-4 h-4 text-[var(--navy)]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Toll Free</div>
                <div className="text-[var(--navy)] font-semibold group-hover:text-[var(--yellow-2)] transition">
                  {BRAND.phone}
                </div>
              </div>
            </a>
            <a href={`mailto:${BRAND.email}`} data-testid="contact-email-link" className="flex items-start gap-3 group">
              <div className="w-10 h-10 rounded-full bg-[var(--yellow)] flex items-center justify-center shrink-0">
                <Mail className="w-4 h-4 text-[var(--navy)]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Email</div>
                <div className="text-[var(--navy)] font-semibold group-hover:text-[var(--yellow-2)] transition">
                  {BRAND.email}
                </div>
              </div>
            </a>
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--yellow)] flex items-center justify-center shrink-0">
                <MapPin className="w-4 h-4 text-[var(--navy)]" />
              </div>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-gray-500">Head Office</div>
                <div className="text-[var(--navy)] font-semibold">{BRAND.address}</div>
              </div>
            </div>
          </div>
        </div>

        <form
          onSubmit={onSubmit}
          data-testid="contact-form"
          className="lg:col-span-7 bg-white border border-gray-200 rounded-xl p-6 md:p-8 shadow-sm"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500">Your name</label>
              <Input name="name" value={form.name} onChange={onChange} placeholder="Aarav Kapoor" data-testid="contact-input-name" className="mt-1.5 h-11 border-gray-300 focus-visible:ring-[var(--yellow-2)]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500">Email</label>
              <Input type="email" name="email" value={form.email} onChange={onChange} placeholder="aarav@example.com" data-testid="contact-input-email" className="mt-1.5 h-11 border-gray-300 focus-visible:ring-[var(--yellow-2)]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500">Phone</label>
              <Input name="phone" value={form.phone} onChange={onChange} placeholder="+91 98xxxx xxxx" data-testid="contact-input-phone" className="mt-1.5 h-11 border-gray-300 focus-visible:ring-[var(--yellow-2)]" />
            </div>
            <div>
              <label className="text-[10px] uppercase tracking-widest text-gray-500">Destination</label>
              <Input name="destination" value={form.destination} onChange={onChange} placeholder="Kashmir, Bali, Switzerland..." data-testid="contact-input-destination" className="mt-1.5 h-11 border-gray-300 focus-visible:ring-[var(--yellow-2)]" />
            </div>
          </div>
          <div className="mt-4">
            <label className="text-[10px] uppercase tracking-widest text-gray-500">Tell us about the trip</label>
            <Textarea name="message" value={form.message} onChange={onChange} rows={4} placeholder="Approx dates, group size, special requests..." data-testid="contact-input-message" className="mt-1.5 border-gray-300 resize-none focus-visible:ring-[var(--yellow-2)]" />
          </div>
          <div className="mt-6 flex items-center justify-between flex-wrap gap-3">
            <p className="text-xs text-gray-500 max-w-sm">
              By submitting you agree to our gentle, occasional travel updates.
            </p>
            <Button
              type="submit"
              disabled={submitting}
              data-testid="contact-submit-btn"
              className="bg-[var(--red)] hover:bg-[var(--red-2)] text-white h-11 px-7"
            >
              {submitting ? "Sending..." : "Send enquiry"}
              <Send className="ml-2 w-4 h-4" />
            </Button>
          </div>
        </form>
      </div>
    </section>
  );
};
